import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { observer } from 'mobx-react'
import moment from 'moment'

import DateRangeIcon from '@mui/icons-material/DateRange'
import { Box, IconButton, MenuItem, Button, FormControlLabel, Switch } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'

import { useDebounce, useStores } from 'admin/hooks'
import * as srv from 'admin/services'
import DateRangePicker from 'admin/components/date-range-picker'
import CustomizedAutocomplete from 'admin/components/autocomplete'
import TextField from 'admin/components/textfield'

import styles from '../styles'

const useStyles = makeStyles(styles)

const WEEKDAY_OPTIONS = [
  { label: 'All Weekdays', value: '' },
  { label: 'Monday', value: 'monday' },
  { label: 'Tuesday', value: 'tuesday' },
  { label: 'Wednesday', value: 'wednesday' },
  { label: 'Thursday', value: 'thursday' },
  { label: 'Friday', value: 'friday' },
  { label: 'Saturday', value: 'saturday' },
  { label: 'Sunday', value: 'sunday' },
]

const JOB_TYPE_OPTIONS = [
  { label: 'All Jobs', value: 'all' },
  { label: 'Pickup', value: 'pickup' },
  { label: 'Delivery', value: 'delivery' },
]
const MODE_OPTIONS = [
  { label: 'Raw route', value: 'raw' },
  { label: 'Aggregated movement', value: 'aggregated' },
]

const VISUALIZATION_OPTIONS = [
  { label: 'All', value: 'all' },
  { label: 'Calendar only', value: 'calendar' },
  { label: 'Flow only', value: 'flow' },
  { label: 'Data table only', value: 'table' },
]

const DriverRouteFilter = () => {
  const classes = useStyles()
  const { reportStore, driverStore } = useStores()

  const [anchorEl, setAnchorEl] = useState(null)
  const [searchText, setSearchText] = useState('')

  const debouncedSearchText = useDebounce(searchText, 400)

  const [rangeDate, setRangeDate] = useState({
    startDate: null,
    endDate: null,
    key: 'selection',
  })
  const hasRequiredDates = Boolean(
    reportStore.driverRouteQuery.startDate && reportStore.driverRouteQuery.endDate,
  )

  useEffect(() => {
    reportStore.updateDriverRouteFilter(
      'startDate',
      rangeDate.startDate ? moment(rangeDate.startDate).format('YYYY-MM-DD') : null,
      true,
    )
    reportStore.updateDriverRouteFilter(
      'endDate',
      rangeDate.endDate ? moment(rangeDate.endDate).format('YYYY-MM-DD') : null,
      true,
    )
  }, [rangeDate])

  useEffect(() => {
    reportStore.updateDriverRouteFilter('search', debouncedSearchText.trim(), true)
  }, [debouncedSearchText])

  const handleSelectDateRange = useCallback((ranges) => {
    setRangeDate(ranges.selection)
  }, [])

  const handleOpenDrivers = useCallback(() => {
    driverStore.fetch({ limit: 200, offset: 0 })
  }, [])

  const driverOptions = useMemo(() => {
    return driverStore.list.map((item) => ({
      key: item.id,
      label: item.name || item.code,
    }))
  }, [driverStore.list])

  const selectedDriver = useMemo(() => {
    const selectedKey = reportStore.driverRouteQuery.driverId
    if (!selectedKey) return null
    return driverOptions.find((item) => item.key === selectedKey) || null
  }, [driverOptions, reportStore.driverRouteQuery.driverId])

  const handleExportCsv = useCallback(async () => {
    const params = {
      startDate: reportStore.driverRouteQuery.startDate,
      endDate: reportStore.driverRouteQuery.endDate,
      driverId: reportStore.driverRouteQuery.driverId,
      weekday: reportStore.driverRouteQuery.weekday,
      jobType: reportStore.driverRouteQuery.jobType,
      search: reportStore.driverRouteQuery.search,
      mode: reportStore.driverRouteQuery.mode || 'raw',
      analysisMode: reportStore.driverRouteQuery.analysisMode || 'exploratory',
      aggregationType: reportStore.driverRouteQuery.aggregationType || 'segment',
      dateField: reportStore.driverRouteQuery.dateField || 'bookingDate',
    }

    const response = await srv.exportDriverRouteAnalysis(params)
    if (!response.ok || !response.data) {
      reportStore.rootStore.notificationStore.setError(
        response?.data?.message || 'Failed to export driver route analysis.',
      )
      return
    }

    const blobUrl = window.URL.createObjectURL(response.data)
    const link = document.createElement('a')
    link.href = blobUrl
    link.download = `driver-route-analysis-${moment().format('YYYYMMDD-HHmmss')}.csv`
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(blobUrl)
  }, [reportStore.driverRouteQuery])

  return (
    <div
      className={classes.upperSection}
      style={{ gap: '1rem', flexWrap: 'wrap', justifyContent: 'flex-start' }}
    >
      <Box className={classes.dateRangePicker}>
        <span className={classes.spanDate}>
          {rangeDate.startDate && rangeDate.endDate
            ? `${moment(rangeDate.startDate).format('DD MMM YYYY')} - ${moment(rangeDate.endDate).format('DD MMM YYYY')}`
            : 'Select date range'}
        </span>
        <IconButton
          size="small"
          onClick={(event) => setAnchorEl(event.currentTarget)}
        >
          <DateRangeIcon />
        </IconButton>
      </Box>
      <DateRangePicker
        anchorEl={anchorEl}
        handleClose={() => setAnchorEl(null)}
        handleSelect={handleSelectDateRange}
        selectionRange={rangeDate}
      />

      <CustomizedAutocomplete
        value={selectedDriver}
        options={driverOptions}
        label="Driver"
        loading={driverStore.loading}
        handleOpen={handleOpenDrivers}
        handleChange={(_, value) => {
          reportStore.updateDriverRouteFilter('driverId', value?.key || null, true)
        }}
        size="small"
      />

      <TextField
        select
        size="small"
        label="Weekday"
        value={reportStore.driverRouteQuery.weekday || ''}
        onChange={(event) =>
          reportStore.updateDriverRouteFilter('weekday', event.target.value, true)
        }
        className={classes.field}
      >
        {WEEKDAY_OPTIONS.map((item) => (
          <MenuItem
            key={item.value || 'all'}
            value={item.value}
          >
            {item.label}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        select
        size="small"
        label="Job Type"
        value={reportStore.driverRouteQuery.jobType || 'all'}
        onChange={(event) =>
          reportStore.updateDriverRouteFilter('jobType', event.target.value, true)
        }
        className={classes.field}
      >
        {JOB_TYPE_OPTIONS.map((item) => (
          <MenuItem
            key={item.value}
            value={item.value}
          >
            {item.label}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        select
        size="small"
        label="Route Mode"
        value={reportStore.driverRouteQuery.mode || 'raw'}
        onChange={(event) => reportStore.updateDriverRouteFilter('mode', event.target.value, true)}
        className={classes.field}
      >
        {MODE_OPTIONS.map((item) => (
          <MenuItem
            key={item.value}
            value={item.value}
          >
            {item.label}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        select
        size="small"
        label="Visualization"
        value={reportStore.driverRouteQuery.visualizationMode || 'all'}
        onChange={(event) =>
          reportStore.updateDriverRouteFilter('visualizationMode', event.target.value, true)
        }
        className={classes.field}
      >
        {VISUALIZATION_OPTIONS.map((item) => (
          <MenuItem
            key={item.value}
            value={item.value}
          >
            {item.label}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        size="small"
        label="Search customer / postal"
        value={searchText}
        onChange={(event) => setSearchText(event.target.value)}
        className={classes.field}
      />
      <FormControlLabel
        control={
          <Switch
            color="secondary"
            checked={reportStore.driverRouteHeatmapEnabled}
            onChange={(event) => reportStore.setDriverRouteHeatmapEnabled(event.target.checked)}
          />
        }
        label="Heat map"
      />

      <Button
        variant="contained"
        color="secondary"
        size="medium"
        onClick={handleExportCsv}
        disabled={!hasRequiredDates}
        title="Download raw route records only (no summaries)."
      >
        Export Raw records CSV
      </Button>
    </div>
  )
}

export default observer(DriverRouteFilter)
